import { Anchor, Badge, Group, Stack, Text } from '@mantine/core'
import { IconClock, IconClockExclamation, IconFile, IconLink } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { ClassroomAttachmentLink } from 'components/ClassroomDeliverable'
import type { AssessmentItem, StudentDeliverableInfo } from '../types'

interface DeliverableInfoProps {
  deliverable: StudentDeliverableInfo | undefined
  /** When true, show "{activityTitle} - {deliverableTitle}" above the file list. */
  showActivityName: boolean
  activityTitle: string
}

function formatSubmittedDate(value: string | null, locale: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Per-student deliverable column. PENDING/SUBMITTED tag + LATE + files. */
export function DeliverableInfo({ deliverable, showActivityName, activityTitle }: DeliverableInfoProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'pt-BR'

  return (
    <Stack spacing={6}>
      <Group spacing={4} align="center" noWrap>
        <Text size="sm" color="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {t('arc_teacher_assessment_drawer.deliverable_label')}
        </Text>
        {(!deliverable || deliverable.status === 'PENDING') && (
          <Badge
            size="sm"
            color="orange"
            leftSection={<IconClock size={12} style={{ display: 'block' }} />}
            styles={{ leftSection: { display: 'flex', alignItems: 'center' } }}
          >
            {t('arc_teacher_assessment_drawer.deliverable.pending')}
          </Badge>
        )}
        {deliverable?.status === 'SUBMITTED' && (
          <Badge size="sm" color="green">
            {t('arc_teacher_assessment_drawer.deliverable.submitted')}
          </Badge>
        )}
      </Group>

      {deliverable?.status === 'SUBMITTED' &&
        deliverable.files.length + deliverable.links.length + deliverable.googleDriveFiles.length > 0 && (
        <Stack spacing={4}>
          {showActivityName && activityTitle && (
            <Text size="xs" weight={500}>
              {activityTitle}
            </Text>
          )}
          {deliverable.files.map(file => (
            <Group key={file.id} spacing={6} noWrap align="center">
              <Anchor
                href={file.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                color="violet"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}
              >
                <IconFile size={15} style={{ flexShrink: 0 }} />
                <Text size="sm" lineClamp={1} style={{ maxWidth: 200 }}>
                  {file.name}
                </Text>
              </Anchor>
              {deliverable.submittedAt && (
                <Text size="xs" color="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  · {formatSubmittedDate(deliverable.submittedAt, locale)}
                </Text>
              )}
              {deliverable.late && (
                <Badge
                  size="xs"
                  color="red"
                  leftSection={<IconClockExclamation size={10} style={{ display: 'block' }} />}
                  styles={{ leftSection: { display: 'flex', alignItems: 'center' } }}
                >
                  {t('arc_teacher_assessment_drawer.deliverable.late')}
                </Badge>
              )}
            </Group>
          ))}
          {/* AR-876: link and Classroom submissions were invisible here — the
              drawer only rendered `files`, but most deliveries carry their
              content in `links`/`googleDriveFiles` (see TaskTrackDelivery). */}
          {deliverable.googleDriveFiles.map(driveFile => (
            <ClassroomAttachmentLink key={driveFile.id} file={driveFile} />
          ))}
          {/* The deliverable column is COL_DELIVERABLE (170px) wide, so each
              link gets its own line and the date/late badge wrap below it. */}
          {deliverable.links.map(link => (
            <Stack key={link.id} spacing={2}>
              <Anchor
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                color="violet"
                title={link.url}
                style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}
              >
                <IconLink size={15} style={{ flexShrink: 0 }} />
                <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                </Text>
              </Anchor>
              <Group spacing={6} noWrap>
                {deliverable.submittedAt && (
                  <Text size="xs" color="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {formatSubmittedDate(deliverable.submittedAt, locale)}
                  </Text>
                )}
                {deliverable.late && (
                  <Badge
                    size="xs"
                    color="red"
                    leftSection={<IconClockExclamation size={10} style={{ display: 'block' }} />}
                    styles={{ leftSection: { display: 'flex', alignItems: 'center' } }}
                  >
                    {t('arc_teacher_assessment_drawer.deliverable.late')}
                  </Badge>
                )}
              </Group>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  )
}

/** Helper: a lesson "has more than one deliverable" when 2+ distinct activities
 *  with `activityHasDeliverable=true` appear across the items. Used to decide
 *  whether to label files with the activity name (per Figma AC). */
export function shouldShowActivityNameInDeliverable(
  itemsAcrossLesson: AssessmentItem[],
): boolean {
  const ids = new Set<string>()
  for (const item of itemsAcrossLesson) {
    if (item.activityHasDeliverable) ids.add(item.activityId)
  }
  return ids.size > 1
}
